import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Navbar } from "@/components/marketing/Navbar";
import { Footer } from "@/components/marketing/Footer";
import { SectionLabel } from "@/components/marketing/SectionLabel";
import { PillButton } from "@/components/marketing/PillButton";
import { api } from "@/lib/api";
import { SmsConsentBox, SMS_CONSENT_TEXT } from "@/components/marketing/SmsConsent";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLES = ["Org Admin", "Requester", "Viewer"];
const INDUSTRIES = [
  "Property & Casualty Insurance",
  "Life & Health Insurance",
  "Claims / SIU",
  "State / Municipal Benefit Program",
  "Healthcare Eligibility",
  "Other",
];
const VOLUMES = ["< 50 / month", "50–250 / month", "250–1,000 / month", "1,000+ / month"];

const field = (k, v) => ({ [k]: v });

export default function RequestAccess() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    industry: "",
    license_number: "",
    monthly_volume: "",
    intended_use: "",
    website: "", // honeypot
    sms_consent: false,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, ...field(k, e?.target ? e.target.value : e) }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/access-requests", { ...form, sms_consent_text: form.sms_consent ? SMS_CONSENT_TEXT : "" });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-oath-bg">
        <Navbar />
        <main className="mx-auto max-w-2xl px-5 pb-24 pt-36">
          <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center" data-testid="access-request-success">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">
              Request received.
            </h1>
            <p className="mt-4 text-oath-secondary">
              Thank you. A Super Admin will review your organization and permitted
              purposes. If approved, you will receive an invitation to the operator
              console at the email you provided. No accounts are created until approval.
            </p>
            <div className="mt-8 flex justify-center">
              <PillButton onClick={() => navigate("/")} variant="dark">BACK TO HOME</PillButton>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oath-bg">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32">
        <header className="animate-fade-up">
          <SectionLabel number="00">INVITE-ONLY ONBOARDING</SectionLabel>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-oath-ink sm:text-5xl">
            Request console access
          </h1>
          <p className="mt-4 max-w-xl text-oath-secondary">
            ResidenceProof has no public sign-up. Tell us about your organization and
            intended use. A Super Admin approves each organization and its
            permitted purposes before any accounts are created.
          </p>
        </header>

        <form
          onSubmit={submit}
          data-testid="access-request-form"
          className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 sm:p-8"
        >
          {/* honeypot (hidden from users) */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
            aria-hidden="true"
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full name" required>
              <Input data-testid="field-full-name" value={form.full_name} onChange={set("full_name")} required placeholder="Jordan Avery" />
            </Field>
            <Field label="Company / organization" required>
              <Input data-testid="field-company" value={form.company} onChange={set("company")} required placeholder="Meridian Mutual" />
            </Field>

            <Field label="Role" required>
              <SelectBox testid="field-role" value={form.role} onChange={set("role")} placeholder="Select role" options={ROLES} />
            </Field>
            <Field label="Work email" required>
              <Input data-testid="field-email" type="email" value={form.email} onChange={set("email")} required placeholder="jordan@company.com" />
            </Field>

            <Field label="Phone" required>
              <Input data-testid="field-phone" value={form.phone} onChange={set("phone")} required placeholder="+1 555 000 0000" />
            </Field>
            <Field label="Industry" required>
              <SelectBox testid="field-industry" value={form.industry} onChange={set("industry")} placeholder="Select industry" options={INDUSTRIES} />
            </Field>

            <Field label="License / registration number" required>
              <Input data-testid="field-license" value={form.license_number} onChange={set("license_number")} required placeholder="NAIC / state ID" />
            </Field>
            <Field label="Monthly case volume" required>
              <SelectBox testid="field-volume" value={form.monthly_volume} onChange={set("monthly_volume")} placeholder="Estimated volume" options={VOLUMES} />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Intended use & permissible purpose" required>
              <Textarea
                data-testid="field-intended-use"
                value={form.intended_use}
                onChange={set("intended_use")}
                required
                rows={4}
                placeholder="Describe how verifications will be used (e.g., underwriting of new homeowner policies, claims occupancy confirmation, benefit eligibility audits)."
              />
            </Field>
          </div>

          <SmsConsentBox checked={form.sms_consent} onToggle={() => setForm((f) => ({ ...f, sms_consent: !f.sms_consent }))} />

          <p className="mt-5 text-xs leading-relaxed text-oath-muted">
            By submitting, you confirm you are authorized to request verifications
            on behalf of this organization under a permissible purpose. This form is
            rate-limited and spam-protected.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-access-request"
              className="group inline-flex items-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "SUBMIT REQUEST"}
              {!loading && <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">→</span>}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">
      {label} {required && <span className="text-oath-accent">*</span>}
    </Label>
    {children}
  </div>
);

const SelectBox = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);
